// src/components/DisponibilitaMezziPanel.js
// Il cuore vivo del coordinamento: registro sempre aggiornato di chi ha cosa, richieste tra
// colleghi con risposta libera, "cosa possiedo" dichiarato. Aperto a chiunque nell'azienda —
// nessuna restrizione qui, per restare fluido come richiesto. La scrittura del Registro
// passa sempre dal flusso tracciato (azione diretta o richiesta confermata), mai a mano
// libera, per non perdere la tracciabilità.
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, serverTimestamp, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { ChevronLeft, Plus, X, Check, Trash2 } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";

const TIPI = {
  materiale: { label:"Materiale", emoji:"📦" },
  attrezzatura: { label:"Attrezzatura", emoji:"🛠️" },
  furgone: { label:"Furgone", emoji:"🚐" },
  auto: { label:"Auto", emoji:"🚗" },
  libera: { label:"Voce libera", emoji:"✏️" },
};

function dataIt(s) { return s ? new Date(s+"T00:00:00").toLocaleDateString("it-IT") : "—"; }
function oggi() { return toLocalDateStr(new Date()); }

export default function DisponibilitaMezziPanel({ onNavigate }) {
  const { currentUser } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();

  const [tab, setTab] = useState("registro");
  const [mezzi, setMezzi] = useState([]);
  const [richieste, setRichieste] = useState([]);
  const [possessi, setPossessi] = useState([]);
  const [showNuovaRichiesta, setShowNuovaRichiesta] = useState(false);
  const [formR, setFormR] = useState({ tipo:"materiale", mezzoId:"", descrizione:"", dataInizio:oggi(), dataFine:"", nota:"" });
  const [nuovoPossesso, setNuovoPossesso] = useState("");

  useEffect(() => {
    if (!effectiveOrgId) return;
    const u1 = onSnapshot(query(collection(db,"mezziAttrezzature"), where("orgId","==",effectiveOrgId)),
      snap => setMezzi(snap.docs.map(d=>({id:d.id,...d.data()}))));
    const u2 = onSnapshot(query(collection(db,"richiesteRisorse"), where("orgId","==",effectiveOrgId)),
      snap => setRichieste(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0))));
    const u3 = onSnapshot(query(collection(db,"possessiDichiarati"), where("orgId","==",effectiveOrgId)),
      snap => setPossessi(snap.docs.map(d=>({id:d.id,...d.data()}))));
    return () => { u1(); u2(); u3(); };
  }, [effectiveOrgId]);

  // Azione diretta, un tocco — "ho preso" / "ho restituito" — senza passare da una richiesta
  // formale, per restare il più fluido possibile.
  const prendiInCarico = async (mezzo) => {
    await updateDoc(doc(db,"mezziAttrezzature",mezzo.id), {
      possessoreUid: currentUser.uid, possessoreNome: currentUser.displayName||"Utente", possessoreDal: oggi(), updatedAt: serverTimestamp(),
    });
    await addDoc(collection(db,"richiesteRisorse"), {
      orgId: effectiveOrgId, tipo: mezzo.tipo, mezzoId: mezzo.id, mezzoNome: mezzo.nome, descrizione:"",
      dataInizio: oggi(), dataFine:"", projectId:null, projectName:"",
      richiedenteUid: currentUser.uid, richiedenteNome: currentUser.displayName||"Utente",
      stato:"confermata", diretto:true, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    addToast(`${mezzo.nome} preso in carico ✅`,"success");
  };

  const segnalaRestituito = async (mezzo) => {
    await updateDoc(doc(db,"mezziAttrezzature",mezzo.id), {
      possessoreUid: null, possessoreNome: null, possessoreDal: null, updatedAt: serverTimestamp(),
    });
    addToast(`${mezzo.nome} segnalato come restituito ✅`,"success");
  };

  const creaRichiesta = async () => {
    if (formR.tipo!=="libera" && formR.tipo!=="materiale" && !formR.mezzoId) { addToast("Seleziona un mezzo","error"); return; }
    if ((formR.tipo==="libera"||formR.tipo==="materiale") && !formR.descrizione.trim()) { addToast("Descrivi cosa ti serve","error"); return; }
    const mezzoSel = mezzi.find(m=>m.id===formR.mezzoId);
    await addDoc(collection(db,"richiesteRisorse"), {
      orgId: effectiveOrgId, tipo: formR.tipo, mezzoId: formR.mezzoId||null, mezzoNome: mezzoSel?.nome||"",
      descrizione: formR.descrizione.trim(), dataInizio: formR.dataInizio, dataFine: formR.dataFine, nota: formR.nota.trim(),
      projectId:null, projectName:"",
      richiedenteUid: currentUser.uid, richiedenteNome: currentUser.displayName||"Utente",
      stato:"richiesta", diretto:false,
      rispostaUid:null, rispostaNome:null, rispostaDisponibile:null, rispostaNota:"",
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    addToast("Richiesta inviata ✅","success");
    setFormR({ tipo:"materiale", mezzoId:"", descrizione:"", dataInizio:oggi(), dataFine:"", nota:"" });
    setShowNuovaRichiesta(false);
  };

  const rispondi = async (r, disponibile) => {
    await updateDoc(doc(db,"richiesteRisorse",r.id), {
      rispostaUid: currentUser.uid, rispostaNome: currentUser.displayName||"Utente",
      rispostaDisponibile: disponibile, rispostaAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    addToast("Risposta inviata ✅","success");
  };

  const confermaRichiesta = async (r) => {
    if (r.mezzoId) {
      await updateDoc(doc(db,"mezziAttrezzature",r.mezzoId), {
        possessoreUid: r.richiedenteUid, possessoreNome: r.richiedenteNome, possessoreDal: oggi(), updatedAt: serverTimestamp(),
      });
    }
    await updateDoc(doc(db,"richiesteRisorse",r.id), { stato:"confermata", updatedAt: serverTimestamp() });
    addToast("Richiesta confermata ✅","success");
  };

  const chiudiRichiesta = async (r) => {
    await updateDoc(doc(db,"richiesteRisorse",r.id), { stato:"conclusa", updatedAt: serverTimestamp() });
    if (r.mezzoId) {
      await updateDoc(doc(db,"mezziAttrezzature",r.mezzoId), { possessoreUid:null, possessoreNome:null, possessoreDal:null, updatedAt: serverTimestamp() });
    }
    addToast("Richiesta conclusa ✅","success");
  };

  const aggiungiPossesso = async () => {
    if (!nuovoPossesso.trim()) return;
    await addDoc(collection(db,"possessiDichiarati"), {
      orgId: effectiveOrgId, uid: currentUser.uid, nome: currentUser.displayName||"Utente",
      descrizione: nuovoPossesso.trim(), createdAt: serverTimestamp(),
    });
    setNuovoPossesso("");
  };

  const rimuoviPossesso = async (p) => { await deleteDoc(doc(db,"possessiDichiarati",p.id)); };

  // Sovrapposizioni — solo un avviso, mai un blocco: la decisione resta sempre umana.
  const sovrapposizioni = (mezzoId, dataInizio, dataFine) => {
    if (!mezzoId || !dataInizio) return [];
    return richieste.filter(r => r.mezzoId===mezzoId && r.stato!=="conclusa" && r.stato!=="annullata" &&
      r.dataInizio && (!dataFine || r.dataInizio <= dataFine) && (!r.dataFine || r.dataFine >= dataInizio));
  };

  const mieRichieste = richieste.filter(r => r.richiedenteUid===currentUser?.uid && r.stato!=="conclusa" && !r.diretto);
  const daRispondere = richieste.filter(r => r.stato==="richiesta" && r.richiedenteUid!==currentUser?.uid &&
    possessi.some(p => p.uid===currentUser?.uid && p.descrizione.toLowerCase().includes((r.descrizione||r.mezzoNome||"").toLowerCase().split(" ")[0])));
  const miePossessi = possessi.filter(p => p.uid===currentUser?.uid);

  return (
    <div style={{padding:20,maxWidth:820,overflowY:"auto",height:"100%"}}>
      <button onClick={()=>onNavigate&&onNavigate({page:"home"})} style={{background:"none",border:"none",cursor:"pointer",color:"#7c3aed",display:"flex",alignItems:"center",gap:4,fontSize:13,fontWeight:600,marginBottom:14}}>
        <ChevronLeft size={15}/> Torna alla Dashboard
      </button>

      <h2 style={{fontSize:18,fontWeight:800,color:"#1e1b4b",marginBottom:4}}>📍 Disponibilità Mezzi e Attrezzature</h2>

      <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#5b21b6"}}>
        💜 Questa sezione funziona bene solo se aggiornata in tempo reale. Se il titolare ha deciso di utilizzarla, va rispettata e popolata — eventuali ritardi dovuti a mancati aggiornamenti saranno oggetto di discussione.
      </div>

      <div style={{display:"flex",gap:6,marginBottom:16,borderBottom:"1px solid #e5e7eb"}}>
        {[["registro","📍 Registro"],["richieste","📋 Richieste"],["possiedo","🎒 Cosa possiedo"]].map(([k,l]) => (
          <button key={k} onClick={()=>setTab(k)}
            style={{padding:"8px 14px",border:"none",background:"none",borderBottom:tab===k?"2px solid #7c3aed":"2px solid transparent",color:tab===k?"#7c3aed":"#6b7280",fontWeight:tab===k?700:500,fontSize:13,cursor:"pointer"}}>
            {l}
          </button>
        ))}
      </div>

      {tab==="registro" && (
        <div>
          {mezzi.length===0 ? (
            <div style={{textAlign:"center",padding:40,color:"#9ca3af",fontSize:12.5}}>Nessun mezzo o attrezzatura ancora registrato — l'admin può aggiungerli dal Parco Mezzi.</div>
          ) : mezzi.map(m => {
            const t = TIPI[m.tipo]||TIPI.attrezzatura;
            const disponibile = !m.possessoreUid;
            return (
              <div key={m.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,marginBottom:6}}>
                <span style={{fontSize:18}}>{t.emoji}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#111827"}}>{m.nome} {m.targa?<span style={{color:"#9ca3af",fontWeight:500}}>· {m.targa}</span>:""}</div>
                  <div style={{fontSize:11,color: disponibile?"#059669":"#d97706"}}>
                    {disponibile ? "✅ Disponibile" : `🔶 In uso a ${m.possessoreNome}${m.possessoreDal?` dal ${dataIt(m.possessoreDal)}`:""}`}
                  </div>
                </div>
                {disponibile ? (
                  <button onClick={()=>prendiInCarico(m)} style={{padding:"6px 12px",borderRadius:7,border:"none",background:"#7c3aed",color:"#fff",fontSize:11.5,fontWeight:700,cursor:"pointer"}}>Prendo in carico</button>
                ) : m.possessoreUid===currentUser?.uid ? (
                  <button onClick={()=>segnalaRestituito(m)} style={{padding:"6px 12px",borderRadius:7,border:"1px solid #059669",background:"#d1fae5",color:"#059669",fontSize:11.5,fontWeight:700,cursor:"pointer"}}>Segnalo restituito</button>
                ) : (
                  <button onClick={()=>{setFormR(f=>({...f,tipo:m.tipo,mezzoId:m.id}));setTab("richieste");setShowNuovaRichiesta(true);}}
                    style={{padding:"6px 12px",borderRadius:7,border:"1px solid #e5e7eb",background:"#fff",color:"#6b7280",fontSize:11.5,fontWeight:700,cursor:"pointer"}}>Richiedi</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab==="richieste" && (
        <div>
          <button onClick={()=>setShowNuovaRichiesta(true)} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",borderRadius:8,border:"none",background:"#7c3aed",color:"#fff",fontSize:12.5,fontWeight:700,cursor:"pointer",marginBottom:14}}>
            <Plus size={14}/> Nuova richiesta
          </button>

          {showNuovaRichiesta && (
            <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:12,padding:16,marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                <span style={{fontSize:13,fontWeight:700}}>Nuova richiesta</span>
                <button onClick={()=>setShowNuovaRichiesta(false)} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={16}/></button>
              </div>
              <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                {Object.entries(TIPI).map(([k,t]) => (
                  <button key={k} type="button" onClick={()=>setFormR(f=>({...f,tipo:k,mezzoId:""}))}
                    style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${formR.tipo===k?"#7c3aed":"#e5e7eb"}`,background:formR.tipo===k?"#f5f3ff":"#fff",color:formR.tipo===k?"#7c3aed":"#6b7280",fontSize:11.5,fontWeight:700,cursor:"pointer"}}>
                    {t.emoji} {t.label}
                  </button>
                ))}
              </div>
              {(formR.tipo==="furgone"||formR.tipo==="auto"||formR.tipo==="attrezzatura") ? (
                <select value={formR.mezzoId} onChange={e=>setFormR(f=>({...f,mezzoId:e.target.value}))}
                  style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12.5,marginBottom:8}}>
                  <option value="">Seleziona {TIPI[formR.tipo].label.toLowerCase()}...</option>
                  {mezzi.filter(m=>m.tipo===formR.tipo).map(m=><option key={m.id} value={m.id}>{m.nome}{m.targa?` (${m.targa})`:""}</option>)}
                </select>
              ) : (
                <input value={formR.descrizione} onChange={e=>setFormR(f=>({...f,descrizione:e.target.value}))} placeholder="Cosa ti serve?"
                  style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12.5,marginBottom:8,boxSizing:"border-box"}}/>
              )}
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                <input type="date" value={formR.dataInizio} onChange={e=>setFormR(f=>({...f,dataInizio:e.target.value}))}
                  style={{flex:1,padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12.5}}/>
                <input type="date" value={formR.dataFine} onChange={e=>setFormR(f=>({...f,dataFine:e.target.value}))} placeholder="Fino a (facoltativo)"
                  style={{flex:1,padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12.5}}/>
              </div>
              {formR.mezzoId && sovrapposizioni(formR.mezzoId, formR.dataInizio, formR.dataFine).length>0 && (
                <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"8px 12px",marginBottom:8,fontSize:11.5,color:"#92400e"}}>
                  ⚠️ Già richiesto da altri per queste date — puoi comunque procedere, la decisione resta a chi coordina.
                </div>
              )}
              <input value={formR.nota} onChange={e=>setFormR(f=>({...f,nota:e.target.value}))} placeholder="Nota (facoltativa)"
                style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12.5,marginBottom:10,boxSizing:"border-box"}}/>
              <button onClick={creaRichiesta} style={{padding:"8px 20px",borderRadius:8,border:"none",background:"#7c3aed",color:"#fff",fontSize:12.5,fontWeight:700,cursor:"pointer"}}>Invia richiesta</button>
            </div>
          )}

          {daRispondere.length>0 && (
            <div style={{marginBottom:18}}>
              <div style={{fontSize:11,fontWeight:700,color:"#d97706",textTransform:"uppercase",marginBottom:8}}>⚠️ Qualcuno cerca qualcosa che hai dichiarato di possedere</div>
              {daRispondere.map(r => (
                <div key={r.id} style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:10,padding:"10px 14px",marginBottom:6}}>
                  <div style={{fontSize:12.5,fontWeight:700,color:"#111827"}}>{r.richiedenteNome} cerca: {r.mezzoNome||r.descrizione}</div>
                  <div style={{fontSize:11,color:"#6b7280",marginBottom:8}}>{dataIt(r.dataInizio)}{r.dataFine?` – ${dataIt(r.dataFine)}`:""}{r.nota?` · ${r.nota}`:""}</div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>rispondi(r,true)} style={{padding:"5px 12px",borderRadius:6,border:"1px solid #059669",background:"#d1fae5",color:"#059669",fontSize:11,fontWeight:700,cursor:"pointer"}}>Sì, disponibile</button>
                    <button onClick={()=>rispondi(r,false)} style={{padding:"5px 12px",borderRadius:6,border:"1px solid #dc2626",background:"#fee2e2",color:"#dc2626",fontSize:11,fontWeight:700,cursor:"pointer"}}>No, mi serve</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{fontSize:11,fontWeight:700,color:"#6b7280",textTransform:"uppercase",marginBottom:8}}>Le mie richieste</div>
          {mieRichieste.length===0 ? (
            <div style={{fontSize:12,color:"#9ca3af"}}>Nessuna richiesta aperta.</div>
          ) : mieRichieste.map(r => (
            <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,marginBottom:6}}>
              <span style={{fontSize:16}}>{TIPI[r.tipo]?.emoji}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12.5,fontWeight:700,color:"#111827"}}>{r.mezzoNome||r.descrizione}</div>
                <div style={{fontSize:11,color:"#6b7280"}}>{dataIt(r.dataInizio)}{r.dataFine?` – ${dataIt(r.dataFine)}`:""} · {r.stato}</div>
                {r.rispostaUid && <div style={{fontSize:11,color:r.rispostaDisponibile?"#059669":"#dc2626"}}>{r.rispostaNome}: {r.rispostaDisponibile?"disponibile":"non disponibile"}</div>}
              </div>
              {r.stato==="richiesta" && r.rispostaDisponibile && (
                <button onClick={()=>confermaRichiesta(r)} style={{padding:"5px 10px",borderRadius:6,border:"none",background:"#7c3aed",color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer"}}>Conferma</button>
              )}
              {r.stato==="confermata" && (
                <button onClick={()=>chiudiRichiesta(r)} style={{padding:"5px 10px",borderRadius:6,border:"1px solid #e5e7eb",background:"#fff",color:"#6b7280",fontSize:11,fontWeight:700,cursor:"pointer"}}>Concludi</button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab==="possiedo" && (
        <div>
          <p style={{fontSize:12,color:"#6b7280",marginBottom:14}}>Facoltativo — dichiara cosa possiedi, così i colleghi possono chiederti in prestito quando serve, senza obbligo di darlo.</p>
          <div style={{display:"flex",gap:8,marginBottom:14}}>
            <input value={nuovoPossesso} onChange={e=>setNuovoPossesso(e.target.value)} placeholder="Es. Trapano Bosch, Scala 3 metri..."
              onKeyDown={e=>e.key==="Enter"&&aggiungiPossesso()}
              style={{flex:1,padding:"8px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12.5}}/>
            <button onClick={aggiungiPossesso} style={{padding:"8px 16px",borderRadius:8,border:"none",background:"#7c3aed",color:"#fff",fontSize:12.5,fontWeight:700,cursor:"pointer"}}>Aggiungi</button>
          </div>
          {miePossessi.length===0 ? (
            <div style={{fontSize:12,color:"#9ca3af"}}>Non hai ancora dichiarato nulla.</div>
          ) : miePossessi.map(p => (
            <div key={p.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,marginBottom:5}}>
              <Check size={13} color="#059669"/>
              <span style={{flex:1,fontSize:12.5,color:"#111827"}}>{p.descrizione}</span>
              <button onClick={()=>rimuoviPossesso(p)} style={{background:"none",border:"none",cursor:"pointer",color:"#dc2626",padding:2}}><Trash2 size={13}/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
